// The global Tribed issue board: one kanban (Board/global) shared by every
// community's admins for platform bugs, feature requests, and improvements.
// This shape is duplicated in the MCP (mcp/src/repos/board.ts) — keep the two
// in sync by hand (there is no shared package).

// The preset tones a stage can wear, matching the outreach pipeline tones so
// TONE_VARIANT / TONE_DOT in leadUtils keep working for board columns.
export type BoardStageTone =
  | "neutral"
  | "info"
  | "accent"
  | "success"
  | "warning"
  | "danger";

export interface BoardStageDef {
  key: string; // slug: [a-z0-9_]+
  label: string;
  tone?: BoardStageTone;
}

export type BoardCardType = "bug" | "feature" | "improvement";

// Which surface the card concerns: the customer-facing mobile app (ThePath),
// this admin dashboard, or the backend (functions, MCP, infrastructure).
export type BoardArea = "customer_app" | "admin" | "backend";

export const BOARD_AREAS: BoardArea[] = ["customer_app", "admin", "backend"];

export const AREA_LABELS: Record<BoardArea, string> = {
  customer_app: "Customer app",
  admin: "Admin dashboard",
  backend: "Backend",
};

export type BoardPriority = "low" | "medium" | "high" | "urgent";

export const BOARD_CARD_TYPES: BoardCardType[] = [
  "bug",
  "feature",
  "improvement",
];

export const BOARD_PRIORITIES: BoardPriority[] = [
  "low",
  "medium",
  "high",
  "urgent",
];

// Higher number = more urgent; used as a secondary sort inside a column.
export const PRIORITY_ORDER: Record<BoardPriority, number> = {
  low: 0,
  medium: 1,
  high: 2,
  urgent: 3,
};

export interface BoardAttachment {
  id: string;
  url: string;
  path: string;
  name: string;
  /** Set on MCP-filed attachments; older admin uploads are images with neither. */
  contentType?: string;
  kind?: "image" | "video" | "audio" | "file";
  sizeBytes?: number;
  createdAt: string;
}

/** Where a card came from. Cards filed before this existed read as "admin". */
export type BoardSource = "admin" | "coach" | "mcp" | "sentry";

export interface BoardCard {
  id: string;
  type: BoardCardType;
  area: BoardArea;
  title: string;
  description: string;
  status: string; // stage key
  priority: BoardPriority;
  position: number; // sparse float ordering within a column
  createdBy: { uid: string; fullName: string };
  creatorCommunityId: string;
  creatorCommunityName: string;
  attachments: BoardAttachment[];
  // Coach requests filed through the MCP (submit_request) carry these: who
  // sent it and whether Tribed's daily triage has stamped it reviewed.
  source?: BoardSource;
  submittedBy?: { name?: string; email?: string; role?: string };
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  reviewedNote?: string;
  sentryIssueId?: string;
  sentryUrl?: string;
  sentryEnvironment?: string;
  sentryRelease?: string | null;
  sentryDist?: string | null;
  sentryPlatform?: string | null;
  sentryBrand?: string | null;
  sentryDevice?: string | null;
  sentryFingerprint?: string | null;
  sentryLastSeenAt?: string;
  sentryEventCount?: number;
  sentryDiagnostics?: {
    eventId?: string;
    eventTimestamp?: string;
    exceptionType?: string;
    exceptionValue?: string;
    mechanism?: string;
    handled?: boolean | null;
    threadName?: string;
    transaction?: string;
    os?: string;
    appIdentifier?: string;
    simulator?: boolean | null;
    traceId?: string;
    sdk?: string;
    stackFrames?: string[];
    breadcrumbs?: string[];
  };
  createdAt: string;
  updatedAt: string;
}

export interface BoardComment {
  id: string;
  text: string;
  author: { uid: string; fullName: string };
  authorCommunityId: string;
  createdAt: string;
}

export const DEFAULT_BOARD_STAGES: BoardStageDef[] = [
  { key: "backlog", label: "Backlog", tone: "neutral" },
  { key: "todo", label: "Todo", tone: "info" },
  { key: "in_progress", label: "In Progress", tone: "accent" },
  { key: "done", label: "Done", tone: "success" },
];

// Everyone can view, add cards, comment, and attach; only Tribed operators can
// move cards between stages, edit the stage set, and delete. Every coach is
// role ADMIN(101) of their own community, so role-based gating cannot separate
// operators from coaches — instead the gate is the admin's home community,
// mirroring the OUTREACH_OWNER_COMMUNITY_ID pattern in functions/src/outreach.ts.
export const BOARD_OPERATOR_COMMUNITY_IDS = ["digital_university", "tribed"];

export const isBoardOperator = (user?: { communityId?: string }): boolean =>
  !!user?.communityId &&
  BOARD_OPERATOR_COMMUNITY_IDS.includes(user.communityId);
